import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '../components/ui/Button'
import { Card, CardContent } from '../components/ui/Card'
import { MainNavigation } from '../components/navigation/MainNavigation'
import { MainFooter } from '../components/navigation/MainFooter'
import { useAuthStore } from '../stores/authStore'
import { NAVIGATION_ACTIONS } from '../config/navigation.config'
import { 
  Home, 
  Wrench, 
  Plane,
  Target,
  TrendingUp,
  DollarSign,
  Clock,
  CheckCircle,
  ArrowRight,
  BarChart3,
  Activity,
  Code,
  Heart,
  Coffee,
  ChevronLeft,
  ChevronRight,
  Baby
} from 'lucide-react'

export const UseCasesPage: React.FC = () => {
  const navigate = useNavigate()
  const { isAuthenticated } = useAuthStore()
  const [activeTab, setActiveTab] = useState(0)

  const handleGetStarted = () => {
    navigate(NAVIGATION_ACTIONS.primary.path)
  }

  const useCases = [
    {
      id: 0,
      title: "One-Person Realtor",
      subtitle: "Agency Member",
      icon: <Home className="h-6 w-6 text-white" />,
      description: "Independent realtor looking to maximize territory potential and optimize marketing spend",
      scenarios: [
        {
          title: "Market Expansion Analysis",
          question: "Should I focus on luxury condos vs. family homes in my territory?",
          challenge: "Limited time to research multiple market segments and identify the most profitable niche",
          solution: "Analyzes local market trends, buyer demographics, price appreciation rates, and competition density to identify the most profitable niche",
          outcome: "Clear strategic focus that maximizes commission potential and reduces wasted prospecting time",
          roi: "$45,000 additional annual commission (150% ROI)"
        },
        {
          title: "Territory Optimization",
          question: "Which neighborhoods should I prioritize for prospecting?",
          challenge: "Uncertain which areas offer the best return on prospecting investment and time",
          solution: "Maps turnover rates, average days on market, commission potential, and competitor activity to create a strategic territory plan",
          outcome: "Data-driven territory plan that focuses efforts on highest-opportunity neighborhoods",
          roi: "$32,000 increased annual revenue (210% ROI)"
        },
        {
          title: "Pricing Strategy",
          question: "How should I price this listing competitively?",
          challenge: "Manual comparative market analysis is time-consuming and may miss key market dynamics",
          solution: "Real-time comparative market analysis with predictive pricing models based on micro-market conditions, seasonal trends, and buyer behavior",
          outcome: "Optimal pricing that sells faster while maximizing seller satisfaction and referrals",
          roi: "$28,000 faster sale closings + repeat business (185% ROI)"
        },
        {
          title: "Lead Generation Strategy",
          question: "Where should I invest my limited marketing budget?",
          challenge: "Multiple marketing channels available but unclear which generate highest-quality leads",
          solution: "Analyzes which marketing channels (social media, local ads, referral programs) generate highest-quality leads in their specific market",
          outcome: "Focused marketing spend that delivers better ROI and more qualified prospects",
          roi: "$38,000 improved lead conversion (320% ROI)"
        }
      ]
    },
    {
      id: 1,
      title: "Car Mechanic Shop",
      subtitle: "5-8 Employees",
      icon: <Wrench className="h-6 w-6 text-white" />,
      description: "Small automotive repair business competing against chain shops and seeking growth opportunities",
      scenarios: [
        {
          title: "Service Line Expansion",
          question: "Should we add transmission repair or focus on general maintenance?",
          challenge: "Major investment decision with unclear demand patterns and competitive landscape",
          solution: "Analyzes local demand patterns, competitor services, profit margins, equipment investment ROI, and skill requirements",
          outcome: "Strategic service expansion that maximizes revenue potential while leveraging existing capabilities",
          roi: "$85,000 annual revenue increase (240% ROI)"
        },
        {
          title: "Customer Acquisition Strategy",
          question: "How do we compete against chain shops like Jiffy Lube?",
          challenge: "Limited marketing budget competing against national chains with brand recognition",
          solution: "Identifies underserved customer segments (luxury car owners, fleet vehicles, specialty services) and optimal positioning strategy",
          outcome: "Differentiated market position that attracts higher-value customers willing to pay premium prices",
          roi: "$65,000 premium service revenue (430% ROI)"
        },
        {
          title: "Pricing Optimization",
          question: "Are we pricing our services competitively while maintaining margins?",
          challenge: "Balancing competitive pricing with profitability across diverse service offerings",
          solution: "Local competitive pricing analysis with demand elasticity modeling to optimize pricing for maximum revenue",
          outcome: "Optimized pricing strategy that improves margins without losing customers to competitors",
          roi: "$42,000 increased profit margins (280% ROI)"
        },
        {
          title: "Inventory Management",
          question: "Which parts should we stock vs. order as needed?",
          challenge: "Tying up cash in slow-moving inventory while risking stockouts on popular items",
          solution: "Analyzes seasonal repair patterns, supplier lead times, and inventory carrying costs to optimize stock levels",
          outcome: "Improved cash flow and customer satisfaction through optimized inventory investment",
          roi: "$25,000 cash flow improvement + reduced stockouts (165% ROI)"
        }
      ]
    },
    {
      id: 2,
      title: "Aircraft Maintenance Shop",
      subtitle: "50 Employees - Parts Overhaul",
      icon: <Plane className="h-6 w-6 text-white" />,
      description: "Specialized aircraft parts overhaul facility serving small aircraft market with growth opportunities",
      scenarios: [
        {
          title: "Market Opportunity Assessment",
          question: "Should we expand into helicopter components or stay focused on small aircraft?",
          challenge: "Major strategic decision requiring deep understanding of market dynamics and regulatory requirements",
          solution: "Analyzes market size, growth rates, regulatory requirements, certification costs, and competitive landscape for each segment",
          outcome: "Data-driven expansion strategy that maximizes growth potential while managing regulatory and investment risks",
          roi: "$450,000 new market revenue potential (300% ROI)"
        },
        {
          title: "Certification ROI Analysis",
          question: "Which FAA certifications will generate the highest return on investment?",
          challenge: "Multiple certification options with significant time and cost investments required",
          solution: "Maps certification costs, timeline, market demand, and pricing premiums to prioritize certification efforts",
          outcome: "Strategic certification roadmap that maximizes revenue impact while optimizing resource allocation",
          roi: "$320,000 certification premium revenue (380% ROI)"
        },
        {
          title: "Customer Diversification Strategy",
          question: "Should we target flight schools, private owners, or charter companies?",
          challenge: "Different customer segments have varying needs, payment terms, and relationship requirements",
          solution: "Analyzes customer segment profitability, payment terms, volume potential, and relationship requirements",
          outcome: "Balanced customer portfolio that reduces risk while maximizing revenue and cash flow stability",
          roi: "$275,000 diversified revenue growth (230% ROI)"
        },
        {
          title: "Competitive Positioning",
          question: "How do we compete against larger MRO facilities?",
          challenge: "Competing against well-funded competitors with broader service offerings and economies of scale",
          solution: "Identifies service gaps, turnaround time advantages, specialization opportunities, and pricing strategies",
          outcome: "Clear competitive advantages that differentiate the business and command premium pricing",
          roi: "$195,000 premium positioning revenue (260% ROI)"
        }
      ]
    },
    {
      id: 3,
      title: "Dental Practice",
      subtitle: "General Dentistry",
      icon: <Activity className="h-6 w-6 text-white" />,
      description: "General dentistry practice focused on preventive care and patient retention with growth opportunities",
      scenarios: [
        {
          title: "Patient Acquisition Strategy",
          question: "Should I focus on family dentistry, cosmetic procedures, or senior care?",
          challenge: "Multiple patient demographics available but unclear which segments offer the best long-term value and growth potential",
          solution: "Analyzes local demographic trends, insurance coverage patterns, treatment profitability, and competitive landscape to identify optimal patient mix",
          outcome: "Strategic patient acquisition plan that maximizes lifetime value while building sustainable practice growth",
          roi: "$125,000 increased patient lifetime value (340% ROI)"
        },
        {
          title: "Service Expansion Analysis",
          question: "Should we add orthodontics, oral surgery, or focus on preventive care?",
          challenge: "Major equipment and training investments with uncertain demand and competitive implications",
          solution: "Evaluates market demand, referral patterns, equipment ROI, training costs, and competitive positioning for each service line",
          outcome: "Data-driven service expansion that leverages existing patient base while maximizing revenue per patient",
          roi: "$185,000 new service line revenue (420% ROI)"
        },
        {
          title: "Insurance vs. Cash Pay Strategy",
          question: "Should we accept more insurance plans or focus on cash-pay patients?",
          challenge: "Balancing patient accessibility with profit margins across different payment methods and insurance reimbursement rates",
          solution: "Analyzes insurance reimbursement trends, cash-pay market size, patient retention rates, and administrative costs by payment type",
          outcome: "Optimized payer mix strategy that maximizes revenue while maintaining patient access and practice efficiency",
          roi: "$75,000 improved profit margins (310% ROI)"
        },
        {
          title: "Location Expansion Planning",
          question: "Should we open a second location or expand our current facility?",
          challenge: "Significant capital investment decision requiring understanding of market saturation, patient travel patterns, and operational complexity",
          solution: "Maps patient demographics, competitor locations, market penetration rates, and operational cost implications for expansion options",
          outcome: "Strategic expansion plan that maximizes market coverage while optimizing operational efficiency and investment returns",
          roi: "$220,000 expansion revenue potential (290% ROI)"
        }
      ]
    },
    {
      id: 4,
      title: "Software Developer Consultant",
      subtitle: "Independent Contractor",
      icon: <Code className="h-6 w-6 text-white" />,
      description: "Independent software developer consultant specializing in web development and seeking to optimize client acquisition and pricing",
      scenarios: [
        {
          title: "Technology Stack Specialization",
          question: "Should I specialize in React/Node.js or expand into mobile development?",
          challenge: "Multiple technology paths available but uncertain which offers the best market demand, pricing power, and long-term career growth",
          solution: "Analyzes job market trends, freelance project demand, hourly rate comparisons, technology adoption rates, and competitive landscape across different tech stacks",
          outcome: "Strategic technology roadmap that maximizes earning potential while building competitive expertise in high-demand areas",
          roi: "$35,000 higher annual rates (290% ROI)"
        },
        {
          title: "Client Acquisition Strategy",
          question: "Should I focus on local small businesses, remote startups, or enterprise contracts?",
          challenge: "Different client segments have varying project sizes, payment terms, relationship requirements, and growth potential",
          solution: "Evaluates client segment profitability, project duration, payment reliability, referral potential, and market saturation for each target market",
          outcome: "Optimized client portfolio strategy that balances project stability, growth potential, and income predictability",
          roi: "$52,000 improved client mix revenue (450% ROI)"
        },
        {
          title: "Pricing and Packaging Strategy",
          question: "Should I charge hourly, project-based, or offer retainer agreements?",
          challenge: "Multiple pricing models available with different risk profiles, client preferences, and revenue predictability implications",
          solution: "Analyzes market pricing trends, client payment preferences, project scope variability, and cash flow implications for different pricing models",
          outcome: "Strategic pricing framework that maximizes revenue while reducing payment risk and improving client relationships",
          roi: "$28,000 pricing optimization gains (380% ROI)"
        },
        {
          title: "Service Expansion Planning",
          question: "Should I add design services, DevOps consulting, or team augmentation?",
          challenge: "Multiple service expansion options with different skill requirements, market demand, and competitive positioning implications",
          solution: "Evaluates market demand, skill gap analysis, competitive landscape, pricing premiums, and client cross-selling opportunities for each service expansion",
          outcome: "Strategic service portfolio that leverages existing skills while capturing higher-value opportunities and client wallet share",
          roi: "$48,000 additional service revenue (520% ROI)"
        }
      ]
    },
    {
      id: 5,
      title: "Health & Wellness Center",
      subtitle: "Fitness & Nutrition",
      icon: <Heart className="h-6 w-6 text-white" />,
      description: "Health and wellness center offering fitness, nutrition counseling, and wellness programs seeking to optimize service mix and growth",
      scenarios: [
        {
          title: "Service Portfolio Optimization",
          question: "Should we focus on personal training, group fitness, or wellness coaching?",
          challenge: "Multiple service options with different profit margins, space requirements, and staff expertise needs",
          solution: "Analyzes local demand patterns, pricing comparisons, staff utilization rates, space optimization, and customer lifetime value by service type",
          outcome: "Strategic service mix that maximizes revenue per square foot while leveraging existing staff capabilities",
          roi: "$95,000 optimized service revenue (265% ROI)"
        },
        {
          title: "Membership Model Strategy",
          question: "Should we offer monthly memberships, class packages, or pay-per-visit pricing?",
          challenge: "Different pricing models impact cash flow, member retention, and competitive positioning in varying ways",
          solution: "Evaluates member retention rates, payment preferences, competitor pricing strategies, and cash flow implications across different membership models",
          outcome: "Optimized membership structure that improves cash flow predictability while maximizing member lifetime value",
          roi: "$68,000 improved retention revenue (320% ROI)"
        },
        {
          title: "Target Demographics Analysis",
          question: "Should we focus on young professionals, seniors, or families with children?",
          challenge: "Different demographic segments have varying needs, spending power, and service preferences requiring different marketing approaches",
          solution: "Analyzes local demographics, spending patterns, health trends, competitor targeting, and service preferences by age group and lifestyle",
          outcome: "Strategic demographic focus that aligns service offerings with highest-value customer segments and market opportunities",
          roi: "$78,000 targeted marketing efficiency (410% ROI)"
        },
        {
          title: "Expansion and Partnership Strategy",
          question: "Should we add nutrition services, partner with healthcare providers, or expand locations?",
          challenge: "Multiple growth paths with different investment requirements, regulatory considerations, and partnership complexity",
          solution: "Evaluates market demand for integrated services, partnership opportunities, regulatory requirements, investment ROI, and competitive advantages",
          outcome: "Strategic growth plan that leverages healthcare trends while optimizing investment returns and operational efficiency",
          roi: "$145,000 partnership revenue potential (380% ROI)"
        }
      ]
    },
    {
      id: 6,
      title: "Bagel Franchise",
      subtitle: "What A Bagel Style",
      icon: <Coffee className="h-6 w-6 text-white" />,
      description: "Fast-casual bagel franchise focused on breakfast and lunch service seeking to optimize operations and market positioning",
      scenarios: [
        {
          title: "Menu Optimization Strategy",
          question: "Should we expand breakfast offerings, add more lunch options, or focus on specialty bagels?",
          challenge: "Limited kitchen space and prep time require strategic menu decisions that balance customer demand with operational efficiency",
          solution: "Analyzes sales data patterns, prep time requirements, ingredient costs, local taste preferences, and competitor menu strategies",
          outcome: "Optimized menu mix that maximizes revenue per transaction while streamlining kitchen operations and reducing waste",
          roi: "$55,000 menu optimization savings (285% ROI)"
        },
        {
          title: "Location Performance Analysis",
          question: "Which locations should receive additional investment vs. operational improvements?",
          challenge: "Multiple locations with varying performance levels require different strategic approaches and investment priorities",
          solution: "Evaluates foot traffic patterns, demographic analysis, competitor proximity, lease terms, and operational metrics across all locations",
          outcome: "Strategic investment allocation that improves underperforming locations while maximizing returns from high-potential sites",
          roi: "$125,000 location optimization gains (340% ROI)"
        },
        {
          title: "Digital Ordering and Delivery Strategy",
          question: "Should we invest in mobile ordering, partner with delivery services, or focus on in-store experience?",
          challenge: "Multiple digital channels available with different cost structures, customer expectations, and operational complexity",
          solution: "Analyzes customer ordering preferences, delivery market penetration, competitor digital strategies, margin implications, and operational capacity",
          outcome: "Strategic digital roadmap that meets customer expectations while maintaining profitability and operational efficiency",
          roi: "$85,000 digital channel revenue (365% ROI)"
        },
        {
          title: "Franchise Growth Planning",
          question: "Should we focus on company-owned expansion or prioritize franchisee recruitment?",
          challenge: "Different growth models have varying capital requirements, control levels, and scaling potential with distinct risk profiles",
          solution: "Evaluates market saturation, capital availability, franchise support capacity, brand consistency requirements, and growth trajectory modeling",
          outcome: "Strategic franchise development plan that balances rapid expansion with brand quality and franchisee success",
          roi: "$450,000 franchise expansion revenue (420% ROI)"
        }
      ]
    },
    {
      id: 7,
      title: "Stay-Home Parent",
      subtitle: "Career Transition Planning",
      icon: <Baby className="h-6 w-6 text-white" />,
      description: "Stay-at-home parent planning strategic career re-entry, business opportunities, or family financial optimization",
      scenarios: [
        {
          title: "Career Re-entry Strategy",
          question: "Should I return to my previous career, pivot to a new field, or start freelancing?",
          challenge: "Skills gap concerns, industry changes during absence, and balancing family responsibilities with career goals",
          solution: "Analyzes job market trends, skill demand, salary comparisons, remote work opportunities, and career progression paths in current vs. new fields",
          outcome: "Strategic career roadmap that maximizes earning potential while accommodating family priorities and schedule flexibility",
          roi: "$42,000 optimized career re-entry income (380% ROI)"
        },
        {
          title: "Home-Based Business Analysis",
          question: "Should I start a consulting business, online store, or service-based venture?",
          challenge: "Limited startup capital, uncertain market demand, and need to balance business growth with parenting responsibilities",
          solution: "Evaluates market opportunities, startup costs, time investment requirements, scalability potential, and competitive landscape for different business models",
          outcome: "Data-driven business plan that leverages existing skills while building sustainable income around family schedule",
          roi: "$35,000 home business revenue potential (450% ROI)"
        },
        {
          title: "Education Investment Planning",
          question: "Should I pursue additional certifications, complete a degree, or focus on skills-based learning?",
          challenge: "Limited time and budget for education while needing to stay competitive in evolving job markets",
          solution: "Analyzes ROI of different educational paths, industry certification values, employer preferences, and flexible learning options",
          outcome: "Strategic education plan that maximizes career impact while fitting within family schedule and budget constraints",
          roi: "$28,000 increased earning potential from targeted education (320% ROI)"
        },
        {
          title: "Family Financial Optimization",
          question: "Should we prioritize saving for children's education, retirement, or family business investment?",
          challenge: "Multiple competing financial priorities with limited household income and complex trade-off decisions",
          solution: "Analyzes family financial goals, education cost projections, retirement planning scenarios, and investment opportunity comparisons",
          outcome: "Comprehensive financial strategy that balances immediate needs with long-term family wealth building and security",
          roi: "$52,000 optimized family financial planning value (290% ROI)"
        }
      ]
    }
  ]

  const stats = [
    { number: '45min', label: 'Analysis Time' },
    { number: '350%', label: 'Average ROI' },
    { number: '$143K', label: 'Avg Revenue Impact' }
  ]

  // Scroll functionality for tab navigation
  const [scrollPosition, setScrollPosition] = useState(0)
  const maxScroll = Math.max(0, useCases.length - 3) // Show 3 items at a time on smaller screens

  const scrollLeft = () => {
    setScrollPosition(Math.max(0, scrollPosition - 1))
  }

  const scrollRight = () => {
    setScrollPosition(Math.min(maxScroll, scrollPosition + 1))
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
      {/* Navigation */}
      <MainNavigation variant="full" />

      {/* Hero Section */}
      <section className="relative py-12 px-4 text-center">
        <div className="absolute inset-0 bg-gradient-to-b from-blue-900/20 to-transparent" />
        <div className="container mx-auto max-w-4xl relative z-10">
          <div className="mb-6">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-4 bg-gradient-to-r from-white to-blue-300 bg-clip-text text-transparent">
              💼 Real Business Use Cases
            </h1>
            <p className="text-xl text-blue-200 mb-4 font-medium">
              See How StratIQX Intelligence Transforms Real Businesses Across Industries
            </p>
          </div>
          
          {/* Stats */}
          <div className="flex flex-wrap justify-center gap-12 mb-6">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-3xl font-bold text-cyan-400 mb-2">
                  {stat.number}
                </div>
                <div className="text-sm text-gray-400 uppercase tracking-wider">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tab Navigation */}
      <section className="py-8 px-4">
        <div className="container mx-auto max-w-6xl">
          {/* Desktop View - Grid Layout */}
          <div className="hidden lg:grid lg:grid-cols-4 xl:grid-cols-8 gap-4 mb-8">
            {useCases.map((useCase, index) => (
              <button
                key={useCase.id}
                onClick={() => setActiveTab(index)}
                className={`flex flex-col items-center justify-center px-3 py-6 rounded-xl transition-all duration-300 ${
                  activeTab === index
                    ? 'bg-gradient-to-r from-blue-600 to-cyan-600 text-white shadow-lg scale-105'
                    : 'bg-slate-800 text-gray-300 hover:bg-slate-700 border border-slate-600'
                }`}
              >
                <div className={`w-12 h-12 rounded-full flex items-center justify-center mb-3 ${
                  activeTab === index ? 'bg-white/20' : 'bg-slate-700'
                }`}>
                  {useCase.icon}
                </div>
                <div className="text-center">
                  <div className="font-semibold text-xs lg:text-sm">{useCase.title}</div>
                  <div className={`text-xs mt-1 ${activeTab === index ? 'text-blue-100' : 'text-gray-500'}`}>
                    {useCase.subtitle}
                  </div>
                </div>
              </button>
            ))}
          </div>

          {/* Mobile/Tablet View - Scrollable Carousel */}
          <div className="lg:hidden mb-8">
            <div className="flex items-center mb-4">
              <button
                onClick={scrollLeft}
                disabled={scrollPosition === 0}
                className={`p-2 rounded-full mr-2 ${
                  scrollPosition === 0 
                    ? 'bg-slate-700 text-gray-500 cursor-not-allowed' 
                    : 'bg-slate-600 text-white hover:bg-slate-500'
                }`}
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              
              <div className="flex-1 overflow-hidden">
                <div 
                  className="flex gap-4 transition-transform duration-300"
                  style={{ transform: `translateX(-${scrollPosition * 33.333}%)` }}
                >
                  {useCases.map((useCase, index) => (
                    <button
                      key={useCase.id}
                      onClick={() => setActiveTab(index)}
                      className={`flex-shrink-0 w-1/3 flex flex-col items-center justify-center px-4 py-6 rounded-xl transition-all duration-300 ${
                        activeTab === index
                          ? 'bg-gradient-to-r from-blue-600 to-cyan-600 text-white shadow-lg scale-105'
                          : 'bg-slate-800 text-gray-300 hover:bg-slate-700 border border-slate-600'
                      }`}
                    >
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center mb-3 ${
                        activeTab === index ? 'bg-white/20' : 'bg-slate-700'
                      }`}>
                        {useCase.icon}
                      </div>
                      <div className="text-center">
                        <div className="font-semibold text-sm">{useCase.title}</div>
                        <div className={`text-xs mt-1 ${activeTab === index ? 'text-blue-100' : 'text-gray-500'}`}>
                          {useCase.subtitle}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
              
              <button
                onClick={scrollRight}
                disabled={scrollPosition === maxScroll}
                className={`p-2 rounded-full ml-2 ${
                  scrollPosition === maxScroll 
                    ? 'bg-slate-700 text-gray-500 cursor-not-allowed' 
                    : 'bg-slate-600 text-white hover:bg-slate-500'
                }`}
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Active Use Case Content */}
          <div className="mb-12">
            <Card className="bg-slate-800 border-blue-800/30 mb-8">
              <CardContent className="p-8 text-center">
                <div className="bg-gradient-to-r from-blue-600 to-cyan-600 w-16 h-16 rounded-xl flex items-center justify-center mx-auto mb-6">
                  {useCases[activeTab].icon}
                </div>
                <h2 className="text-3xl font-bold text-white mb-4">
                  {useCases[activeTab].title}
                </h2>
                <p className="text-lg text-gray-300 max-w-2xl mx-auto">
                  {useCases[activeTab].description}
                </p>
              </CardContent>
            </Card>

            {/* Scenarios Grid */}
            <div className="grid md:grid-cols-2 gap-6">
              {useCases[activeTab].scenarios.map((scenario, index) => (
                <Card key={index} className="bg-slate-800 border-blue-800/30 hover:border-cyan-500/50 transition-all duration-300 group">
                  <CardContent className="p-6">
                    <div className="flex items-center mb-4">
                      <div className="bg-gradient-to-r from-amber-600 to-orange-500 w-10 h-10 rounded-full flex items-center justify-center mr-4">
                        <Target className="h-5 w-5 text-white" />
                      </div>
                      <h3 className="text-xl font-semibold text-white">
                        {scenario.title}
                      </h3>
                    </div>
                    
                    <div className="space-y-4">
                      <div>
                        <h4 className="text-sm font-semibold text-blue-400 mb-2 flex items-center">
                          <BarChart3 className="h-4 w-4 mr-2" />
                          Business Question
                        </h4>
                        <p className="text-gray-300 text-sm italic">
                          "{scenario.question}"
                        </p>
                      </div>
                      
                      <div>
                        <h4 className="text-sm font-semibold text-red-400 mb-2 flex items-center">
                          <Clock className="h-4 w-4 mr-2" />
                          Current Challenge
                        </h4>
                        <p className="text-gray-300 text-sm">
                          {scenario.challenge}
                        </p>
                      </div>
                      
                      <div>
                        <h4 className="text-sm font-semibold text-green-400 mb-2 flex items-center">
                          <CheckCircle className="h-4 w-4 mr-2" />
                          StratIQX Intelligence Solution
                        </h4>
                        <p className="text-gray-300 text-sm">
                          {scenario.solution}
                        </p>
                      </div>
                      
                      <div>
                        <h4 className="text-sm font-semibold text-cyan-400 mb-2 flex items-center">
                          <TrendingUp className="h-4 w-4 mr-2" />
                          Business Outcome
                        </h4>
                        <p className="text-gray-300 text-sm">
                          {scenario.outcome}
                        </p>
                      </div>

                      <div className="bg-gradient-to-r from-green-900/20 to-emerald-900/20 border border-green-500/30 rounded-lg p-4">
                        <h4 className="text-sm font-semibold text-green-400 mb-2 flex items-center">
                          <DollarSign className="h-4 w-4 mr-2" />
                          Expected ROI
                        </h4>
                        <p className="text-green-300 text-sm font-semibold">
                          {scenario.roi}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 bg-slate-800/50">
        <div className="container mx-auto max-w-4xl text-center">
          <div className="bg-gradient-to-r from-slate-800 to-blue-900 border border-blue-700/50 rounded-2xl p-8 backdrop-blur-lg">
            <h2 className="text-3xl font-bold text-white mb-4">
              Ready to Transform Your Business Like These Examples?
            </h2>
            <p className="text-lg text-gray-300 mb-6 max-w-2xl mx-auto leading-relaxed">
              Join businesses across industries who are making smarter strategic decisions with StratIQX Intelligence.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Button 
                onClick={handleGetStarted}
                size="lg"
                className="bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white px-8 py-4 text-lg shadow-xl flex items-center"
              >
                Start Your Strategic Analysis
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              
              <div className="text-sm text-gray-400">
                <div className="flex items-center justify-center">
                  <CheckCircle className="h-4 w-4 text-green-400 mr-2" />
                  Average 350% ROI
                </div>
                <div className="flex items-center justify-center mt-1">
                  <CheckCircle className="h-4 w-4 text-green-400 mr-2" />
                  45-minute delivery
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <MainFooter variant="full" />
    </div>
  )
}